import { useState } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { StaticVideo } from '@/types/video.types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import classNames from 'classnames';
import { getLinkThumbnail } from '@/utils/video/link-thumbnail';
import images from '@/utils/images';

type TSessionVodLinkInfoProps = {
  sessionVideo: StaticVideo | null;
  isEditMode?: boolean;
  onReset?: () => void;
  onSave?: (linkUrl: string, videoName: string, thumbnailUrl?: string) => void;
};

const SessionVodLinkInfo = ({ sessionVideo, isEditMode = false, onReset, onSave }: TSessionVodLinkInfoProps) => {
  const [linkUrl, setLinkUrl] = useState(sessionVideo?.type === 'url' ? sessionVideo?.source : '');
  const [thumbnailGenerating, setThumbnailGenerating] = useState(false);

  if (isEditMode) {
    return (
      <Container
        className="flex-column ai-center gap-4"
        height="84px"
        width="100%"
        background="var(--global-cont-1)"
        padding="12px"
        borderRadius="8px"
      >
        <Container className="flex ai-center jc-space-between padding-h-8 gap-4" width="100%">
          <Text className="L2" color="var(--gray-500)">
            Add URL
          </Text>
          <Container className="flex ai-center gap-12">
            <Button padding="0" onClick={onReset}>
              <Text className="S2" color={getThemeColorCSSVariable(EColorVariablesV2.primary)}>
                Reset
              </Text>
            </Button>
            {/* TODO: implement add thumbnail
            <Text className="L2" color="var(--global-cont-3)">|</Text>
            <Button padding="0" onClick={() => {}}>
              <Text className="S2" color={getThemeColorCSSVariable(EColorVariablesV2.primary)}>
                Add thumbnail
              </Text>
            </Button> */}
          </Container>
        </Container>
        <InputWithOverlay
          inputHeight="32px"
          wrapperClass={classNames('width-100-percent', { 'opacity-70': thumbnailGenerating })}
          inputProps={{
            placeholder: 'Enter video URL',
            className: 'P2',
            value: linkUrl,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setLinkUrl(e.target.value),
          }}
          onDebounce={(value) => {
            if (value && onSave) {
              setThumbnailGenerating(true);
              getLinkThumbnail(value)
                .then((thumbnail) => {
                  onSave(value, 'Linked Video', thumbnail);
                })
                .catch((e) => {
                  console.error(e);
                  onSave(value, 'Linked Video');
                })
                .finally(() => {
                  setThumbnailGenerating(false);
                });
            }
          }}
          debounceTime={250}
        />
      </Container>
    );
  }

  if (!sessionVideo) return null;

  const thumbnail = sessionVideo.thumbnail_url ? sessionVideo.thumbnail_url.trim() : images.VideoLinkPlaceholer;

  return (
    <Container className="flex ai-center jc-flex-start" borderRadius="8px" padding="12px" height="84px" width="100%">
      <Container className="flex ai-center jc-center gap-12" height="100%" width="100px">
        <img
          src={thumbnail}
          className="max-width-100-percent max-height-100-percent radius-8 image-cover"
          style={{ aspectRatio: '16/9' }}
        />
      </Container>
      <Container className="flex-column" borderRadius="8px" padding="8px" width="100%">
        <Text className="S3" clamp={1} color="var(--gray-100)">
          {sessionVideo?.source}
        </Text>
        <Text className="L2" color="var(--gray-300)">
          URL
        </Text>
      </Container>
    </Container>
  );
};

export default SessionVodLinkInfo;
